import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Mail, Phone, MapPin, Calendar, GraduationCap, Hash, Loader2 } from "lucide-react";
import API from "../api/api01";
import { toast } from "sonner";

interface ProfileProps {
  user: {
    name: string;
    email: string;
    id?: string;
  };
}

export function Profile({ user: initialUser }: ProfileProps) {
  const [profileData, setProfileData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Use the ID from initialUser or fallback to localStorage
  const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = initialUser?.id || userFromStorage?.id;

  useEffect(() => {
    if (studentId) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [studentId]);

  const fetchProfile = async () => {
    try {
      const response = await API.get(`/superadmin/profile/${studentId}`);
      setProfileData(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile details");
    } finally {
      setLoading(false);
    }
  };

  // Generate Dicebear avatar based on email
  const getAvatarUrl = (email: string) =>
    `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
      email || "user"
    )}&backgroundColor=6366F1`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const user = profileData || {
    ...initialUser,
    rollNo: "N/A",
    department: "N/A",
    semester: "N/A",
    batch: "N/A",
    phone: "N/A",
    address: "N/A",
    dateOfBirth: "N/A",
    bloodGroup: "N/A",
    cgpa: "0.0",
    attendance: "0%",
    credits: "0",
    rank: "N/A"
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto bg-background min-h-screen">
      <div>
        <h1 className="text-2xl font-semibold">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">
          View and manage your personal information
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1 bg-card border-white/10 shadow-sm">
          <CardContent className="pt-6 flex flex-col items-center text-center space-y-4">
            <Avatar className="h-24 w-24 border-4 border-primary/20">
              <AvatarImage src={getAvatarUrl(user.email)} alt={user.name} />
              <AvatarFallback className="bg-primary/10 text-primary text-xl">
                {user.name
                  ?.split(" ")
                  .map((n: string) => n[0])
                  .join("")
                  .slice(0, 2) || "U"}
              </AvatarFallback>
            </Avatar>

            <div className="space-y-2">
              <h3 className="text-lg font-medium">{user.name}</h3>
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
                {user.rollNo}
              </Badge>
              <p className="text-sm text-muted-foreground">
                {user.department}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="lg:col-span-2 bg-card border-white/10 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Personal Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoItem icon={Hash} label="Roll Number" value={user.rollNo} />
              <InfoItem icon={GraduationCap} label="Department" value={user.department} />
              <InfoItem icon={Calendar} label="Semester" value={user.semester} />
              <InfoItem icon={Calendar} label="Batch" value={user.batch} />
              <InfoItem icon={Mail} label="Email" value={user.email} />
              <InfoItem icon={Phone} label="Phone" value={user.phone} />
              <InfoItem icon={MapPin} label="Address" value={user.address} />
              <InfoItem icon={Calendar} label="Date of Birth" value={user.dateOfBirth} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Academic Summary */}
      <Card className="bg-card border-white/10 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Academic Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="CGPA" value={user.cgpa} />
            <StatCard label="Attendance" value={user.attendance} />
            <StatCard label="Credits Earned" value={user.credits} />
            <StatCard label="Rank" value={user.rank} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Info item with icon
function InfoItem({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-white/5">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
      <div className="space-y-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm">{value}</p>
      </div>
    </div>
  );
}

// Academic stat card
function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4 rounded-lg border border-white/5 bg-background shadow-inner text-center">
      <p className="text-2xl font-semibold text-primary dark:text-primary">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
